import { sectionLabels } from "../section-labels";
import { colorFields, buttonFields } from "./_shared";
/**
 * Stories ("Our stories") — editorial section with a featured story plus
 * stacked secondary stories. Block-driven: each `story` block carries
 * tag, title, description, image, readTime, and an optional link. The
 * first block renders as the big featured story; remaining blocks render
 * in the stacked column to the right.
 *
 * `readAllLabel` + `readAllHref` drive the section's top-right CTA. The
 * standard `colorFields` + `buttonFields` from `_shared.ts` give the
 * seller per-section background, text color, button style, and a
 * hide-button toggle for the Read-all CTA.
 */
export const storiesSchema = {
    type: "stories",
    name: sectionLabels["stories"],
    description: {
        ar: "قسم تحريري فيه قصة مميزة وقصص إضافية تقدر تزودها وتعدّلها.",
        en: "Editorial journal section with a featured story and additional stacked stories.",
    },
    // Unlocked: deletable. NOT re-addable from "+ Add section" — see
    // `offeredInAddMenu` below; recovery is undo or a version restore.
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    // Renders from its own content — nothing on a custom page is missing. Held
    // deliberately while `offeredInAddMenu: false` makes it unreachable: this is
    // the flag that decides WHERE the type may go, and it becomes live again the
    // day the menu offers stories.
    availableOnCustomPage: true,
    // Furniture, not a menu item: 1 preset, 2 demo instances. An editorial
    // journal — a featured story plus stacked ones, each with a tag, a read time
    // and body copy — is a magazine's section, and the Egyptian social seller
    // this editor is for does not have that copy to write. The only one of the
    // seven that is a SPEC §8 original rather than a port, so this reverses an
    // original decision on evidence rather than trimming an import.
    offeredInAddMenu: false,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "نوع العرض", en: "Layout" },
            helpText: {
                ar: "طريقة عرض القصص.",
                en: "How the stories are laid out.",
            },
            options: [
                { value: "story-editorial", label: { ar: "تحريري", en: "Editorial" } },
                { value: "story-grid", label: { ar: "شبكة", en: "Grid" } },
                { value: "story-list", label: { ar: "قائمة", en: "List" } },
                { value: "story-carousel", label: { ar: "شرائح", en: "Carousel" } },
                { value: "story-overlay", label: { ar: "تراكب", en: "Overlay" } },
            ],
            default: "story-editorial",
        },
        {
            // C3: the Overlay card lays its caption OVER the image, and used to drop
            // the story `description` outright — the seller could author one and never
            // see it. Rather than force it on (which would wreck the variant's whole
            // point) or hide the field (which would take away text they wrote), the
            // seller decides. Overlay-only: every other layout always renders it.
            //
            // Defaults to `hidden` = exactly today's rendering, so no existing store
            // changes appearance on deploy; showing it is an opt-in.
            id: "overlayDescription",
            type: "select",
            label: {
                ar: "وصف القصة فوق الصورة",
                en: "Story description on the photo",
            },
            helpText: {
                ar: "التراكب بيحط النص فوق الصورة. تقدر تخفي الوصف، أو تظهره لما الماوس يعدّي، أو تخليه ظاهر دايمًا. على الموبايل «عند المرور» بيظهر دايمًا (مافيش hover في اللمس).",
                en: 'Overlay puts the caption on the photo. Hide the description, reveal it on hover, or always show it. On phones "On hover" always shows — touch has no hover.',
            },
            options: [
                { value: "hidden", label: { ar: "مخفي", en: "Hidden" } },
                { value: "hover", label: { ar: "عند المرور", en: "On hover" } },
                { value: "always", label: { ar: "ظاهر دايمًا", en: "Always" } },
            ],
            default: "hidden",
            showIf: { variant: "story-overlay" },
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 60 },
            default: { ar: "قصصنا", en: "Our stories" },
        },
        {
            id: "eyebrow",
            type: "text",
            label: { ar: "نص صغير فوق العنوان", en: "Eyebrow label" },
            helpText: {
                ar: "النص الصغير فوق العنوان (مثال: اليوميات).",
                en: "Small uppercase label above the title (e.g. JOURNAL).",
            },
            validate: { max: 30 },
            default: "JOURNAL",
        },
        {
            id: "readAllLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص زر اقرأ الكل", en: "Read-all label" },
            helpText: {
                ar: 'النص الظاهر على زر "اقرأ الكل" في أعلى يمين القسم.',
                en: 'Label shown on the top-right "Read all" button.',
            },
            validate: { max: 30 },
            default: { ar: "اقرأ الكل", en: "Read all" },
        },
        {
            id: "readAllHref",
            type: "url",
            label: { ar: "رابط اقرأ الكل", en: "Read-all link" },
            helpText: {
                ar: 'الرابط اللي بيفتحه زر "اقرأ الكل".',
                en: 'Where the "Read all" button takes the buyer.',
            },
        },
        ...colorFields(),
        ...buttonFields(),
    ],
    blocks: [
        {
            type: "story",
            name: { ar: "قصة", en: "Story" },
            description: {
                ar: "قصة واحدة بصورة وعنوان وملخص ووقت قراءة.",
                en: "A single editorial entry with image, title, body, and read time.",
            },
            maxPerSection: 8,
            settings: [
                {
                    id: "tag",
                    type: "text",
                    i18n: true,
                    label: { ar: "الوسم", en: "Tag" },
                    helpText: {
                        ar: "العلامة الصغيرة فوق العنوان (مثل CRAFT, CARE).",
                        en: "Small uppercase label above the title (e.g. CRAFT).",
                    },
                    validate: { max: 30 },
                },
                {
                    id: "title",
                    type: "text",
                    i18n: true,
                    label: { ar: "العنوان", en: "Title" },
                    validate: { max: 100 },
                },
                {
                    id: "description",
                    type: "textarea",
                    i18n: true,
                    label: { ar: "الوصف", en: "Description" },
                    // C3: every layout renders this. Overlay used to drop it silently;
                    // it now follows the section's `overlayDescription` setting, so the
                    // hint points at that control instead of claiming "never shown".
                    helpText: {
                        ar: "بيظهر في كل الأشكال. في «تراكب» بيتحكم فيه إعداد «وصف القصة فوق الصورة».",
                        en: 'Shown in every layout. In Overlay it follows the "Story description on the photo" setting.',
                    },
                    validate: { max: 400 },
                },
                {
                    id: "image",
                    type: "image",
                    focalPoint: true,
                    label: { ar: "الصورة", en: "Image" },
                },
                {
                    id: "readTime",
                    type: "text",
                    i18n: true,
                    label: { ar: "وقت القراءة", en: "Read time" },
                    helpText: {
                        ar: 'مثل "6 MIN READ".',
                        en: 'E.g. "6 MIN READ".',
                    },
                    validate: { max: 30 },
                },
                {
                    id: "href",
                    type: "url",
                    label: { ar: "الرابط", en: "Link" },
                    helpText: {
                        ar: "الصفحة اللي بتفتح لما المشتري يضغط على القصة.",
                        en: "Where the story card takes the buyer.",
                    },
                },
            ],
        },
    ],
};
//# sourceMappingURL=stories.js.map